import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CiSearch } from "react-icons/ci";
import { GoOrganization, GoFileSymlinkFile } from "react-icons/go";
import { MdDomainDisabled } from "react-icons/md";
import axios from "axios";
import { notification } from "antd";
import SystemLevelConfig from "./SystemLevelConfig";
import "../../styles/OrgServerConfiguration.css";
import openNotification from "components/utils/CustomNotification";
import SystemConfigManageTeam from "./SystemConfigManageTeam";

const beAddress =
  window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

const SessionCheck = () => true;
const SessionExtend = () => {};
const useCheckSessionHeartBeat = () => {};

interface ConfirmationDialogProps {
  show: boolean;
  message: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  show,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
}) => {
  if (!show) return null;
  return (
    <div className="confirmation-overlay">
      <div className="confirmation-box">
        <p className="confirmation-message">{message}</p>
        <div className="confirmation-buttons">
          <button className="cancel-btn" onClick={onCancel}>
            {cancelText}
          </button>
          <button className="disable-btn" onClick={onConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

interface OrgData {
  org_uid: string;
  user: string;
  enabled: boolean;
  initialized: boolean;
  db?: {
    type?: string;
    host?: string;
    port?: number;
    user?: string;
    password?: string;
    protocol?: string;
  };
  email?: {
    type?: string;
    smtp?: {
      enable?: string;
      type?: string;
      host?: string;
      port?: number;
      user?: string;
      password?: string;
      security?: {
        enable?: string;
        ca_file?: string;
        cert_file?: string;
        key_file?: string;
        type?: string;
      };
    };
  };
}

const OrgServerConfiguration: React.FC = () => {
  useCheckSessionHeartBeat();
  const [orgData, setOrgData] = useState<OrgData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<OrgData | null>(null);
  const [activeTab, setActiveTab] = useState<"org" | "system">("org");
  const navigate = useNavigate();
  const isSessionValid = SessionCheck();

  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const handleSearch = (query: string) => setSearchQuery(query);

  const fetchOrgConfigs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${beAddress}/api/org-configs`, {
        headers: { "user-uid": userUid },withCredentials:true,
      });
      const orgs: OrgData[] = Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      setOrgData(orgs);
      SessionExtend();
    } catch {
      openNotification({
        type: "error",
        message: "Failed to fetch organization configurations.",
      });
      setOrgData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSessionValid) fetchOrgConfigs();
  }, [isSessionValid]);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      // Clear the state so refresh won't flip it back
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const setOrgLoading = (
    orgUid: string,
    action: string,
    isLoading: boolean
  ) => {
    setLoadingStates((prev) => ({
      ...prev,
      [`${orgUid}_${action}`]: isLoading,
    }));
  };

  const buildDbPayload = (org: OrgData) => ({
    type: org.db?.type || "",
    host: org.db?.host || "",
    port: org.db?.port ?? "",
    user: org.db?.user || "",
    password: org.db?.password || "",
    protocol: org.db?.protocol || "",
  });

  const buildEmailPayload = (org: OrgData) => {
    if (!org.email) return undefined;
    const payload: any = {
      type: org.email.type || "smtp",
    };
    
    if (org.email.smtp) {
      payload.smtp = {
        enable: org.email.smtp.enable || "No",
        type: org.email.smtp.type || "smtp",
        host: org.email.smtp.host || "",
        port: org.email.smtp.port || 587,
        user: org.email.smtp.user || "",
        password: org.email.smtp.password || "",
        security: {
          enable: org.email.smtp.security?.enable || "No",
          ca_file: org.email.smtp.security?.ca_file || "",
          cert_file: org.email.smtp.security?.cert_file || "",
          key_file: org.email.smtp.security?.key_file || "",
          type: org.email.smtp.security?.type || "tls/self-signed",
        },
      };
    }
    
    return payload;
  };

  const handleEnableOrg = async (org: OrgData) => {
    setSubmitLoading(true);
    setOrgLoading(org.org_uid, "enable", true);
    try {
      const payload = {
        db: buildDbPayload(org),
        org_uid: org.org_uid,
        user: org.user,
        enabled: true,
        initialized: false,
        email: buildEmailPayload(org),
      };
      const res = await axios.put(`${beAddress}/api/org-db`, payload, {
        headers: { "user-uid": userUid },withCredentials:true,
      });
      openNotification({
        type: res.data.status === "Success" ? "success" : "error",
        message: res.data.message || "",
      });
      fetchOrgConfigs();
    } catch (err: any) {
      openNotification({
        type: "error",
        message: err.response?.data?.message || "Failed to enable database.",
      });
    } finally {
      setSubmitLoading(false);
      setOrgLoading(org.org_uid, "enable", false);
    }
  };

  const handleInitializeOrg = async (org: OrgData) => {
    setSubmitLoading(true);
    setOrgLoading(org.org_uid, "init", true);
    try {
      const payload = {
        db: buildDbPayload(org),
        org_uid: org.org_uid,
        user: org.user,
        enabled: true,
        initialized: false,
        email: buildEmailPayload(org),
      };
      const res = await axios.post(
        `${beAddress}/api/org-db-initialize`,
        payload,
        {
          headers: { "user-uid": userUid },withCredentials:true,
        }
      );
      openNotification({
        type: res.data.status === "Success" ? "success" : "error",
        message: res.data.message || "",
      });
      fetchOrgConfigs();
    } catch (err: any) {
      openNotification({
        type: "error",
        message:
          err.response?.data?.message || "Failed to initialize database.",
      });
    } finally {
      setSubmitLoading(false);
      setOrgLoading(org.org_uid, "init", false);
    }
  };

  // ✅ Disable Org implementation
  const handleDisableOrg = async () => {
    if (!orgToDelete) return;
    setSubmitLoading(true);
    try {
      const res = await axios.delete(`${beAddress}/api/org-config`, {
        headers: { "user-uid": userUid },withCredentials:true,
        data: orgToDelete.org_uid,
      });
      openNotification({
        type: res.data.status === "Success" ? "success" : "error",
        message: res.data.message || "Organization disabled successfully.",
      });
      fetchOrgConfigs();
    } catch (err: any) {
      openNotification({
        type: "error",
        message:
          err.response?.data?.message || "Failed to disable organization.",
      });
    } finally {
      setSubmitLoading(false);
      setShowDialog(false);
      setOrgToDelete(null);
    }
  };

  const filteredOrgs = orgData.filter((org) =>
    org.org_uid.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading)
    return (
      <div style={{ padding: 20 }}>
        <div className="spinner">Loading...</div>
      </div>
    );

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 15 }}>
        Configuration
      </h2>

      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        <button
          style={{
            padding: "8px 16px",
            fontWeight: 600,
            borderRadius: 6,
            border: "1px solid #d1d5db",
            background: activeTab === "org" ? "#5DECE7" : "#f3f4f6",
            color: "#000",
            cursor: "pointer",
          }}
          onClick={() => setActiveTab("org")}
        >
          Org Config
        </button>
        <button
          style={{
            padding: "8px 16px",
            fontWeight: 600,
            borderRadius: 6,
            border: "1px solid #d1d5db",
            background: activeTab === "system" ? "#5DECE7" : "#f3f4f6",
            color: "#000",
            cursor: "pointer",
          }}
          onClick={() => {
            setActiveTab("system");
            navigate("/system-org-manage-team");
          }}
        >
          System Config
        </button>
      </div>

      {activeTab === "org" && (
        <>
          <div className="org-config-controls">
            <div className="search-container">
              <CiSearch className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="search-input"
              />
            </div>

            <button
              className="add-org-button"
              onClick={() =>
                navigate("/server-config", { state: { mode: "add" } })
              }
            >
              + Add Org Config
            </button>
          </div>

          {/* <div className="info-banner">
            ⚠️ Changes made on this page will take effect only after server
            restart.
          </div> */}

          <div className="org-cards">
            {filteredOrgs.length > 0 ? (
              filteredOrgs.map((org) => (
                <div key={org.org_uid} className="org-card">
                  <div className="org-card-header">
                    <div className="org-title">
                      <GoOrganization size={25} />
                      <h4>{org.org_uid}</h4>
                    </div>
                    <div className="org-card-actions">
                      <button
                        title="View Configuration"
                        onClick={() =>
                          navigate("/org-manage-teams", {
                            state: { orgDetails: org, mode: "view" },
                          })
                        }
                      >
                        <GoFileSymlinkFile fontSize="18px" />
                      </button>
                      {org.enabled && (
                        <button
                          title="Disable Organization"
                          onClick={() => {
                            setOrgToDelete(org);
                            setShowDialog(true);
                          }}
                        >
                          <MdDomainDisabled fontSize="18px" color="red" />
                        </button>
                      )}
                    </div>
                  </div>

                  <p>
                    <strong>Admin:</strong> {org.user}
                  </p>

                  {org.email && (
                    <p>
                      <strong>Email:</strong> {org.email.type?.toUpperCase() || 'SMTP'}
                      {org.email.smtp?.host && (
                        <span> ({org.email.smtp.host}:{org.email.smtp.port || 587})</span>
                      )}
                    </p>
                  )}

                  <div className="org-card-buttons">
                    {!org.enabled ? (
                      <>
                        <button
                          disabled={
                            loadingStates[`${org.org_uid}_enable`] ||
                            submitLoading
                          }
                          onClick={() => handleEnableOrg(org)}
                          className="enable-button"
                        >
                          {loadingStates[`${org.org_uid}_enable`]
                            ? "Enabling..."
                            : "Enable"}
                        </button>
                        <div className="init-disabled">✗ Initialize</div>
                      </>
                    ) : (
                      <>
                        <div className="enabled-status">✓ Enabled</div>
                        {!org.initialized ? (
                          <button
                            onClick={() => handleInitializeOrg(org)}
                            disabled={
                              submitLoading ||
                              loadingStates[`${org.org_uid}_init`]
                            }
                            className="enable-button"
                          >
                            {loadingStates[`${org.org_uid}_init`]
                              ? "Initializing..."
                              : "Initialize"}
                          </button>
                        ) : (
                          <div className="enabled-status">✓ Initialized</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p style={{color:"var(--text-color)"}}>No organizations found.</p>
            )}
          </div>

          {/* ✅ Confirmation Dialog */}
          <ConfirmationDialog
            show={showDialog}
            message="Are you sure you want to Disable this Org ?"
            confirmText="Disable"
            cancelText="Cancel"
            onConfirm={handleDisableOrg}
            onCancel={() => setShowDialog(false)}
          />
        </>
      )}

      {activeTab === "system" && <SystemLevelConfig />}
    </div>
  );
};

export default OrgServerConfiguration;
